/* eslint-disable no-param-reassign */
import { PureComponent } from 'react';
import PropTypes from 'prop-types';
import debug from 'debug';
import isEqual from 'lodash/isEqual';
import { TIME } from 'app-webtech-core/helpers/time';
import { AD_EVENT_NAMES, AD_POD_EVENT_NAMES } from '../constants';
import { differenceInMilliseconds, parseISO } from 'date-fns';

const log = debug('core:podTracker:ads');

// For develop: useful page to do date calculus
// https://www.timeanddate.com/date/dateadded.html?d1=29&m1=6&y1=2018&h1=0&i1=11&s1=34&
//
class PodTrackerAdsContainer extends PureComponent {
  static propTypes = {
    adPodWatched: PropTypes.func.isRequired,
    adsData: PropTypes.object.isRequired,
    adsDataReceived: PropTypes.bool,
    children: PropTypes.node,
    cmEvent: PropTypes.func.isRequired,
    contentId: PropTypes.string.isRequired,
    seriesId: PropTypes.string,
    episodeDuration: PropTypes.number,
    playerInstance: PropTypes.object,
    streamStartOffsetFromStartOfEpisode: PropTypes.number,
    isVod: PropTypes.bool.isRequired,
    isTV: PropTypes.bool.isRequired,
    showVideoControls: PropTypes.func.isRequired,
    hideVideoControls: PropTypes.func.isRequired,
    saveLastPos: PropTypes.func.isRequired,
    videoLoaded: PropTypes.func.isRequired,
    onAdStart: PropTypes.func,
    viewedVodAdPods: PropTypes.objectOf(PropTypes.shape({})).isRequired,
    vodLastPosition: PropTypes.objectOf(PropTypes.number).isRequired,
    playerEventsList: PropTypes.func.isRequired,
  };

  static defaultProps = {
    adsDataReceived: false,
    children: null,
    seriesId: null,
    episodeDuration: null,
    playerInstance: null,
    streamStartOffsetFromStartOfEpisode: null,
    onAdStart: null,
  };

  componentDidMount() {
    this.attachEventsToPlayer(this.props.playerInstance);
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.playerInstance && nextProps.playerInstance !== this.props.playerInstance) {
      this.attachEventsToPlayer(nextProps.playerInstance);
    }

    if (nextProps.contentId !== this.props.contentId) {
      // when switching channel, first all contentId is changed, then the playerInstance is changed
      clearInterval(this.savePosInterval);
      this.saveVodPosition();
      if (this.props.playerInstance) {
        this.onSwitchChannel(); // switch from VOD to LiveTV, or changeChannel
      }
    }

    if (!isEqual(nextProps.adsData.adBreaks, this.props.adsData.adBreaks)) {
      const currentPosition = this.getCurrentPlayerPosition();
      const currentAdPod = this.getCurrentAdPod(currentPosition);
      if (currentAdPod) {
        this.closeCurrentAdPod(currentAdPod, currentPosition);
      }
      this.prepareAdsEvents(nextProps.adsData);
    }
  }

  onPause = () => {
    const currentAdBreak = this.getCurrentAdBreak();
    if (currentAdBreak) {
      log('Cannot pause while on an ad.');
    } else {
      this.props.playerInstance.__originalPause();
    }
  };

  onSeek = (newPos) => {
    const { playerInstance, adsDataReceived, adsData } = this.props;
    const newPosMs = 1000 * newPos;
    if (!adsDataReceived) {
      log("Player not yet ready. Can't seek");
      return;
    }
    if (adsData && adsData.adBreaks) {
      const previousAdPods = this.getAdPodsBeforePos(newPosMs);
      // check we already saw all previous AdPods;
      const { viewedVodAdPods, contentId } = this.props;
      const viewed = viewedVodAdPods[contentId];
      for (let i = 0; i < previousAdPods.length; i += 1) {
        if (!viewed || !viewed[i]) {
          if (previousAdPods[i].adBreakDuration > 100) {
            // adBreaks should be at least 100ms long
            log('You must see AdPod ', i, ' before continue');
            playerInstance.__originalSeek(previousAdPods[i].adBreakOffsetFromStartOfEpisode / 1000);
            return;
          }

          this.props.adPodWatched({
            vodContentId: this.props.contentId,
            adPodIndex: i,
            val: true,
          });
        }
      }
    }
    playerInstance.__originalSeek(newPos);
  };

  /**
   * If the user changes channel in the middle of an Ad, we still need to fire cmPodEnd
   * (and cmAdEnd for current Ad)
   */
  onSwitchChannel = () => {
    const currentPosition = this.getCurrentPlayerPosition();
    const currentAdPod = this.getCurrentAdPod(currentPosition);
    this.closeCurrentAdPod(currentAdPod, currentPosition);
  };

  onReady = () => {
    const {
      playerInstance,
      showVideoControls,
      videoLoaded,
      vodLastPosition,
      isVod,
      isTV,
      contentId,
      seriesId,
    } = this.props;
    showVideoControls();
    if (isTV) {
      // VOD doesn't care about firstFrame, as the ads are in seconds from the begining
      // but for LiveTV the ads position is in Date, according to Stitcher's clock.
      this.firstFrameDelay = playerInstance.getStartTimeOffset();
    }
    if (isVod) {
      if (!playerInstance.__originalSeek) {
        playerInstance.__originalSeek = playerInstance.seek;
        playerInstance.seek = this.onSeek;
        this.resumeLastPosition();
      }

      if (!playerInstance.__originalPause) {
        playerInstance.__originalPause = playerInstance.pause;
        playerInstance.pause = this.onPause;
      }
    }
    videoLoaded();

    clearInterval(this.savePosInterval);
    this.savePosInterval = setInterval(() => {
      this.saveVodPosition();
    }, 10 * TIME.ONE_SEC_IN_MS);
  };

  onTime = () => {
    if (this.cmEventsToFire.length === 0) {
      return;
    }

    // not using 'data' argument, as we want it in MS and with firstFrame delay
    const pos = this.getCurrentPlayerPosition();

    // if we are starting an AdPod that we already saw, just skip it.
    const { viewedVodAdPods, adsData, isVod } = this.props;
    if (!adsData || !adsData.adBreaks) {
      return;
    }
    if (isVod && viewedVodAdPods) {
      const currentAdPod = this.getCurrentAdBreak();
      if (currentAdPod) {
        const viewedContent = viewedVodAdPods[this.props.contentId];
        const viewed = viewedContent && viewedContent[currentAdPod.adPodIndex];
        if (viewed) {
          const end = currentAdPod.adBreakOffsetFromStartOfEpisode + currentAdPod.adBreakDuration;
          this.props.playerInstance.__originalSeek(end / 1000);
          return;
        }
      }
    }

    // 1- fire events that needs to be fired right now.
    while (this.cmEventsToFire.length > 0) {
      const adPodEvents = this.cmEventsToFire.pop();

      // Remove AdPods that already started and finished. (ie adPod totally missed)
      if (
        adPodEvents.length > 0 &&
        adPodEvents[adPodEvents.length - 1].name === AD_POD_EVENT_NAMES.AD_POD_0_PERCENT &&
        adPodEvents[0].when < pos
      ) {
        // adPodEvents[0] is AD_POD_EVENT_NAMES.AD_POD_100_PERCENT
        continue; // eslint-disable-line no-continue
      }
      while (adPodEvents.length > 0) {
        const event = adPodEvents.pop();
        if (event.when < pos) {
          const missedBy = Math.trunc(pos - event.when);
          this.props.cmEvent({
            name: event.name,
            ad: event.ad,
            adBreakDuration: event.adBreakDuration,
            missedBy,
          });
          if (
            this.props.isVod && // do not remember AdPod viewed for LiveTV
            (event.name === AD_POD_EVENT_NAMES.AD_POD_0_PERCENT ||
              event.name === AD_POD_EVENT_NAMES.AD_POD_100_PERCENT)
          ) {
            this.showControls(event.name === AD_POD_EVENT_NAMES.AD_POD_100_PERCENT);
            if (event.name === AD_POD_EVENT_NAMES.AD_POD_0_PERCENT) {
              if (typeof this.props.onAdStart === 'function') {
                this.props.onAdStart();
              }
              this.saveVodPosition();
              this.currentAdPodIndex = event.adPodIndex;
            } else {
              this.props.adPodWatched({
                vodContentId: this.props.contentId,
                adPodIndex: event.adPodIndex,
                val: true,
              });
              this.currentAdPodIndex = -1;
              this.saveVodPosition();
            }
          }
        } else {
          adPodEvents.push(event);
          break;
        }
      }
      if (adPodEvents.length > 0) {
        this.cmEventsToFire.push(adPodEvents);
        break;
      }
    }
  };

  getCurrentAdBreak() {
    const { adsData } = this.props;
    if (adsData && adsData.adBreaks) {
      const pos = this.getCurrentPlayerPosition();
      return adsData.adBreaks.find(
        (ad) =>
          ad.adBreakOffsetFromStartOfEpisode < pos &&
          pos < ad.adBreakOffsetFromStartOfEpisode + ad.adBreakDuration,
      );
    }
    return null;
  }

  getCurrentAdPod = (currentPosition) => {
    const { cmEventsToFire } = this;
    if (cmEventsToFire.length === 0) {
      return null;
    }

    // Are we in the middle of an AdPod?
    const currentAdPod = cmEventsToFire.find((adPod) => {
      const adPod100Percent = adPod[0];
      const adPodEnd = adPod100Percent.when;
      const adPodStart = adPodEnd - adPod100Percent.adBreakDuration;
      return adPodStart < currentPosition && currentPosition < adPodEnd;
    });

    return currentAdPod;
  };

  getCurrentPlayerPosition() {
    // In MS From Begining of the Episode/Movie
    const { streamStartTime, streamStartOffsetFromStartOfEpisode, isTV } = this.props;
    if (isTV) {
      const currPosInMsFromBeginingOfEpisode =
        differenceInMilliseconds(new Date(), parseISO(streamStartTime)) +
        streamStartOffsetFromStartOfEpisode +
        this.firstFrameDelay;
      return currPosInMsFromBeginingOfEpisode;
    }
    const pos = 1000 * this.props.playerInstance.getCurrentTime();
    return pos + streamStartOffsetFromStartOfEpisode + this.firstFrameDelay;
  }

  getAdPodsBeforePos(pos) {
    const { adBreaks } = this.props.adsData;
    return adBreaks.filter((brk) => brk.adBreakOffsetFromStartOfEpisode < pos);
  }

  closeCurrentAdPod = (currentAdPod, currentPosition) => {
    if (!currentAdPod) {
      return;
    }
    const cmAdEnd = currentAdPod
      .slice()
      .reverse()
      .find((ad) => ad.name === AD_EVENT_NAMES.AD_100_PERCENT && ad.when > currentPosition);
    let missedBy = Math.trunc(currentPosition - cmAdEnd.when);
    this.props.cmEvent({
      name: cmAdEnd.name,
      ad: cmAdEnd.ad,
      adBreakDuration: cmAdEnd.adBreakDuration,
      missedBy,
    });

    const cmPodEnd = currentAdPod.shift();
    missedBy = Math.trunc(currentPosition - cmPodEnd.when);
    this.props.cmEvent({
      name: cmPodEnd.name,
      adBreakDuration: cmPodEnd.adBreakDuration,
      missedBy,
    });
  };

  resumeLastPosition = () => {
    const { playerInstance, vodLastPosition, contentId, seriesId } = this.props;

    try {
      this.firstFrameDelay = 0;
      const lastPos = vodLastPosition[contentId];

      if (lastPos) {
        playerInstance.__originalSeek(lastPos / 1000);
      } else {
        const seriesPos = vodLastPosition[seriesId] || [];
        const [, episodePos] = seriesPos.find(([episodeId]) => episodeId === contentId) || [];

        if (episodePos) {
          playerInstance.__originalSeek(episodePos / 1000);
        }
      }
    } catch (err) {
      console.error('error while restoring last play position', err);
    }
  };

  showControls(state) {
    if (state) {
      this.props.showVideoControls();
    } else {
      this.props.hideVideoControls();
    }
  }

  saveVodPosition() {
    if (this.currentAdPodIndex >= 0) {
      return; // we are in the middle of an AdPod;
    }
    if (this.props.playerInstance && this.props.isVod && this.props.contentId) {
      const currentPosition = this.getCurrentPlayerPosition();
      this.props.saveLastPos({
        vodContentId: this.props.contentId,
        position: currentPosition,
        vodSeriesId: this.props.seriesId,
        vodEpisodeDuration: this.props.episodeDuration,
      });
    }
  }

  attachEventsToPlayer(playerInstance) {
    if (!playerInstance) {
      return;
    }
    const { playerEventsList } = this.props;

    playerInstance.off(playerEventsList.PLAYER_TIMEUPDATE, this.onTime);
    playerInstance.on(playerEventsList.PLAYER_TIMEUPDATE, this.onTime);

    playerInstance.off(playerEventsList.PLAYER_PLAY, this.onReady);
    playerInstance.once(playerEventsList.PLAYER_PLAY, this.onReady);
  }

  cmEventsToFire = [];
  // The player doesn't start immediately, so the events should be fired a bit later (liveTV only).
  firstFrameDelay = 0;
  // index of the current AdBreak. So we know if we are in the middle of an AdBreak or not.
  currentAdPodIndex = -1;

  prepareAdsEvents = (adsData) => {
    if (!adsData || !adsData.adBreaks || adsData.adBreaks.length === 0) {
      return;
    }
    this.cmEventsToFire = [];

    adsData.adBreaks.forEach((adBreak) => {
      const { adBreakDuration, adBreakOffsetFromStartOfEpisode, ads } = adBreak;
      if (adBreakDuration < 100) {
        // adBreaks should be at least 100ms long
        return;
      }

      const adPodEvents = [];
      const { adPodIndex } = adBreak;
      adPodEvents.push({
        name: AD_POD_EVENT_NAMES.AD_POD_0_PERCENT,
        when: adBreakOffsetFromStartOfEpisode,
        adBreakDuration,
        adPodIndex,
      });
      let base = adBreakOffsetFromStartOfEpisode;

      ads.forEach((ad, adIndex) => {
        const adDuration = ad.duration;
        adPodEvents.push({
          name: AD_EVENT_NAMES.AD_0_PERCENT,
          when: base,
          ad,
          adIndex,
          adPodIndex,
          adBreakDuration,
        });
        adPodEvents.push({
          name: AD_EVENT_NAMES.AD_25_PERCENT,
          when: base + adDuration * 0.25,
          ad,
          adIndex,
          adPodIndex,
          adBreakDuration,
        });
        adPodEvents.push({
          name: AD_EVENT_NAMES.AD_50_PERCENT,
          when: base + adDuration * 0.5,
          ad,
          adIndex,
          adPodIndex,
          adBreakDuration,
        });
        adPodEvents.push({
          name: AD_EVENT_NAMES.AD_75_PERCENT,
          when: base + adDuration * 0.75,
          ad,
          adIndex,
          adPodIndex,
          adBreakDuration,
        });
        adPodEvents.push({
          name: AD_EVENT_NAMES.AD_100_PERCENT,
          when: base + adDuration,
          ad,
          adIndex,
          adPodIndex,
          adBreakDuration,
        });
        base += adDuration;
      });

      adPodEvents.push({
        name: AD_POD_EVENT_NAMES.AD_POD_100_PERCENT,
        when: adBreakOffsetFromStartOfEpisode + adBreakDuration,
        adBreakDuration,
        adPodIndex,
      });
      this.cmEventsToFire.push(adPodEvents.reverse());
    });
    this.cmEventsToFire.reverse(); // order matters, so we can remove the early ones first.
  };

  render() {
    return this.props.children;
  }
}

export { PodTrackerAdsContainer };
